import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { TOURS } from "./tours";
import type { TourDefinition, TourStep } from "./types";
import { GuideSpotlight } from "./GuideSpotlight";

const STORAGE_KEY = "dallali:guide:completed:v1";

function loadCompleted(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

interface GuideContextValue {
  /** Start a tour. By default a no-op if already completed; pass force to replay. */
  startTour: (tourId: string, opts?: { force?: boolean }) => void;
  /** Start a tour ONCE per user (first-visit auto-start). Safe to call on mount. */
  autoStart: (tourId: string) => void;
  stop: () => void;
  isCompleted: (tourId: string) => boolean;
  activeTourId: string | null;
}

const GuideContext = createContext<GuideContextValue | null>(null);

export function GuideProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [completed, setCompleted] = useState<Set<string>>(loadCompleted);
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);

  const persistCompleted = useCallback((next: Set<string>) => {
    setCompleted(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
    } catch {
      /* storage disabled — tour just replays next session, harmless */
    }
  }, []);

  const isCompleted = useCallback((id: string) => completed.has(id), [completed]);

  const tour: TourDefinition | null = activeTourId ? TOURS[activeTourId] ?? null : null;
  const step: TourStep | null = tour ? tour.steps[stepIndex] ?? null : null;

  const startTour = useCallback(
    (tourId: string, opts?: { force?: boolean }) => {
      if (!TOURS[tourId]) return;
      if (!opts?.force && completed.has(tourId)) return;
      setStepIndex(0);
      setActiveTourId(tourId);
    },
    [completed],
  );

  const autoStart = useCallback(
    (tourId: string) => {
      if (completed.has(tourId)) return;
      startTour(tourId);
    },
    [completed, startTour],
  );

  const finish = useCallback(() => {
    if (activeTourId) persistCompleted(new Set(completed).add(activeTourId));
    setActiveTourId(null);
    setStepIndex(0);
  }, [activeTourId, completed, persistCompleted]);

  const stop = useCallback(() => finish(), [finish]);

  // When a step declares a route the user isn't on yet, navigate there first.
  useEffect(() => {
    if (step?.route && window.location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [step, navigate]);

  const next = useCallback(() => {
    if (!tour) return;
    if (stepIndex >= tour.steps.length - 1) {
      finish();
    } else {
      setStepIndex((i) => i + 1);
    }
  }, [tour, stepIndex, finish]);

  const prev = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const value = useMemo<GuideContextValue>(
    () => ({ startTour, autoStart, stop, isCompleted, activeTourId }),
    [startTour, autoStart, stop, isCompleted, activeTourId],
  );

  return (
    <GuideContext.Provider value={value}>
      {children}
      {tour && step && (
        <GuideSpotlight
          tour={tour}
          step={step}
          stepIndex={stepIndex}
          stepCount={tour.steps.length}
          onNext={next}
          onPrev={prev}
          onSkip={finish}
        />
      )}
    </GuideContext.Provider>
  );
}

// Safe no-op when no provider is mounted. GuideProvider sits at the App root in
// production, so this only applies to isolated component/unit tests that render a
// guide-using screen without the provider — there, tours simply don't run (the
// guide is non-critical UI). Avoids forcing every such test to wrap the provider.
const NOOP_GUIDE: GuideContextValue = {
  startTour: () => {},
  autoStart: () => {},
  stop: () => {},
  isCompleted: () => false,
  activeTourId: null,
};

// eslint-disable-next-line react-refresh/only-export-components -- the hook is co-located with its provider (mirrors useLanguage/useAuth); a one-export split isn't worth a new file here.
export function useGuide(): GuideContextValue {
  return useContext(GuideContext) ?? NOOP_GUIDE;
}
